import { useEffect, useState } from "react";
import { Compass, Plus, X, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// Reusable "Define your interests" picker. Lives on the user record, so a fan's
// picks follow them into the Creator Dashboard "Interests" tab on upgrade.
// Discovery matches these against each silo's category + tags (ranked-with-fill).
export const InterestsPicker = ({ compact = false, onCount }) => {
  const [interests, setInterests] = useState([]);
  const [categories, setCategories] = useState([]);
  const [max, setMax] = useState(50);
  const [typed, setTyped] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/me/interests");
        setInterests(r.data.interests || []);
        setCategories(r.data.categories || []);
        setMax(r.data.max || 50);
        onCount?.((r.data.interests || []).length);
      } catch { /* noop */ } finally { setLoading(false); }
    })();
  }, []);

  const add = (raw) => {
    const norm = (raw || "").trim().toLowerCase().replace(/\s+/g, " ").slice(0, 40);
    if (!norm) return;
    if (interests.includes(norm)) return;
    if (interests.length >= max) { toast.error(`That's the max of ${max} interests.`); return; }
    setInterests([...interests, norm]);
    setDirty(true);
  };
  const remove = (it) => { setInterests(interests.filter((x) => x !== it)); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/me/interests", { interests });
      setInterests(r.data.interests || []);
      onCount?.((r.data.interests || []).length);
      setDirty(false);
      toast.success("Interests saved — your Discover feed will match these.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save interests.");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-sm text-[#6B6A68]" data-testid="interests-loading">Loading your interests…</div>;

  return (
    <div data-testid="interests-picker" className={compact ? "" : "bg-white border-2 hairline rounded-xl p-5 sm:p-6"}>
      <div className="flex items-center gap-2 mb-1">
        <Compass size={18} weight="duotone" className="text-[#4A5D4E]" />
        <h3 className="font-serif-display text-xl text-[#1C1B1A]">Define your interests</h3>
      </div>
      <p className="text-sm text-[#6B6A68] mb-4 max-w-2xl">
        Pick the topics you care about — your Discover feed surfaces matching silos first, then fills in the rest. Choose from categories or type your own. Up to {max}.
      </p>

      {/* Category quick-picks */}
      <div className="flex flex-wrap gap-1.5 mb-4" data-testid="interests-categories">
        {categories.map((c) => {
          const on = interests.includes(c.value);
          return (
            <button
              key={c.value}
              type="button"
              data-testid={`interest-cat-${c.value}`}
              onClick={() => (on ? remove(c.value) : add(c.value))}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-[#4A5D4E] text-white border-[#4A5D4E]" : "border-[#E5E0D5] text-[#6B6A68] hover:border-[#4A5D4E] hover:text-[#4A5D4E]"}`}
            >
              {on ? "✓ " : "+ "}{c.label}
            </button>
          );
        })}
      </div>

      {/* Free-type add */}
      <div className="flex items-center gap-2 mb-4">
        <input
          data-testid="interest-input"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(typed); setTyped(""); } }}
          placeholder="Type a topic and press Enter… (e.g. stoicism, startups, jazz)"
          className="flex-1 bg-white border hairline rounded-md px-4 py-2.5 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] text-sm"
        />
        <button
          type="button"
          data-testid="interest-add-btn"
          onClick={() => { add(typed); setTyped(""); }}
          className="shrink-0 px-3 py-2.5 rounded-md border hairline text-[#4A5D4E] hover:bg-[#4A5D4E] hover:text-white flex items-center gap-1 text-sm"
        >
          <Plus size={14} weight="bold" /> Add
        </button>
      </div>

      {/* Selected chips */}
      <div className="flex flex-wrap items-center gap-1.5 mb-4 min-h-[28px]" data-testid="interests-chips">
        {interests.length === 0 ? (
          <span className="text-xs text-[#A89360] italic">No interests yet — pick a few above.</span>
        ) : (
          interests.map((it, i) => (
            <span key={`${it}-${i}`} data-testid={`interest-chip-${i}`}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#A89360]/15 text-[#7A6A3A] text-xs">
              {it}
              <button type="button" onClick={() => remove(it)} data-testid={`interest-remove-${i}`} className="hover:text-[#C16E5A]" aria-label={`Remove ${it}`}>
                <X size={10} weight="bold" />
              </button>
            </span>
          ))
        )}
      </div>

      <div className="flex items-center justify-between">
        <span className="text-xs text-[#6B6A68]">{interests.length}/{max}</span>
        <button
          type="button"
          data-testid="interests-save-btn"
          onClick={save}
          disabled={saving || !dirty}
          className="text-sm px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-40 flex items-center gap-1.5"
        >
          <FloppyDisk size={14} weight="duotone" /> {saving ? "Saving…" : dirty ? "Save interests" : "Saved"}
        </button>
      </div>
    </div>
  );
};

export default InterestsPicker;
